/**
 * SF-3180 Task 2 — pure state helpers for the role-mapping rule editor.
 *
 * The editor manages an ordered list of `RoleToTypeRule`s. First match wins
 * on the backend (see sf-auth-service `src/utils/roleMapping.ts`), so order
 * is semantic — reordering changes behaviour. All helpers are pure so the
 * component's state updates stay trivially testable without rendering.
 *
 * Validation mirrors the backend (`sf-auth-service/src/routes/ssoConfig.ts`
 * `validateRoleToType`) so the admin gets inline feedback before save. The
 * backend is still authoritative — we re-validate on PATCH in Task 4.
 */
import type { RoleToTypeRule, RoleMatchType } from './types';
import { isValidUserType } from './userTypes';

/** Bounds match the backend heuristic in `sf-auth-service/src/utils/userTypes.ts`. */
export const MAX_REGEX_LENGTH = 512;

export interface RuleDraft {
  match: RoleMatchType;
  /** Used for regex match; for exact-add this is the empty string. */
  value: string;
  /** Used for exact multi-add (one rule per value); ignored for regex. */
  values: string[];
  type: string;
}

export function emptyDraft(): RuleDraft {
  return { match: 'exact', value: '', values: [], type: 'employee' };
}

// ---------------------------------------------------------------------------
// List mutations (pure, immutable)
// ---------------------------------------------------------------------------

export function addRule(rules: RoleToTypeRule[], draft: RuleDraft): RoleToTypeRule[] {
  return [...rules, { match: draft.match, value: draft.value, type: draft.type }];
}

/**
 * Add-mode expansion: for `exact` match, expands `draft.values` into N
 * separate `{ match: 'exact', value, type }` rules (each value maps to the
 * same user type). For `regex`, appends a single rule from `draft.value`.
 *
 * Edit-mode still goes through `updateRule` with a single value.
 */
export function addRulesFromDraft(
  rules: RoleToTypeRule[],
  draft: RuleDraft
): RoleToTypeRule[] {
  if (draft.match === 'exact') {
    const expanded: RoleToTypeRule[] = draft.values.map((value) => ({
      match: 'exact' as const,
      value,
      type: draft.type,
    }));
    return [...rules, ...expanded];
  }
  return [...rules, { match: 'regex', value: draft.value, type: draft.type }];
}

export function updateRule(
  rules: RoleToTypeRule[],
  index: number,
  draft: RuleDraft
): RoleToTypeRule[] {
  if (index < 0 || index >= rules.length) return rules;
  const next = rules.slice();
  next[index] = { match: draft.match, value: draft.value, type: draft.type };
  return next;
}

export function deleteRule(rules: RoleToTypeRule[], index: number): RoleToTypeRule[] {
  if (index < 0 || index >= rules.length) return rules;
  const next = rules.slice();
  next.splice(index, 1);
  return next;
}

export function moveRule(
  rules: RoleToTypeRule[],
  from: number,
  to: number
): RoleToTypeRule[] {
  if (from === to) return rules;
  if (from < 0 || from >= rules.length) return rules;
  if (to < 0 || to >= rules.length) return rules;
  const next = rules.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

// ---------------------------------------------------------------------------
// Validation (mirrors sf-auth-service validateRoleToType)
// ---------------------------------------------------------------------------

export interface DraftErrors {
  value?: string;
  type?: string;
  /** Whole-draft error (e.g. "regex too long"). */
  form?: string;
}

export function validateDraft(draft: RuleDraft): DraftErrors {
  const errs: DraftErrors = {};

  if (draft.match === 'exact') {
    const values = Array.isArray(draft.values) ? draft.values : [];
    if (values.length > 0) {
      // add-mode: multi-value tag input — every entered tag must be non-empty
      if (values.some((v) => typeof v !== 'string' || v.length === 0)) {
        errs.value = 'Value is required';
      }
    } else {
      // edit-mode (or add-mode before any tags entered): single-value input
      const value = typeof draft.value === 'string' ? draft.value : '';
      if (value.length === 0) {
        errs.value = 'Value is required';
      }
    }
  } else {
    const value = typeof draft.value === 'string' ? draft.value : '';
    if (value.length === 0) {
      errs.value = 'Value is required';
    } else if (value.length > MAX_REGEX_LENGTH) {
      errs.value = `Regex is too long (max ${MAX_REGEX_LENGTH} characters)`;
    } else {
      try {
        new RegExp(value);
      } catch {
        errs.value = 'Not a valid regular expression';
      }
    }
  }

  if (!isValidUserType(draft.type)) {
    errs.type = 'Target user type is required';
  }

  return errs;
}

export function isDraftValid(draft: RuleDraft): boolean {
  const errs = validateDraft(draft);
  return !errs.value && !errs.type && !errs.form;
}
